import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowDown,
  ArrowUp,
  Bike,
  Footprints,
  MapPin,
  Route,
  Send,
  Undo2,
} from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'

import type { GeoPoint } from '@aeris/contracts'
import {
  Badge,
  Button,
  CheckboxField,
  EmptyState,
  InlineAlert,
  Section,
  Select,
  Spinner,
  notify,
} from '@aeris/ui'

import {
  ApiError,
  cancelDeliveryRoute,
  dispatchDeliveryRoute,
  fetchCouriers,
  fetchDeliveryRoutes,
  fetchRouteQueue,
  planDeliveryRoutes,
  rearrangeDeliveryRoutes,
  removeFromRouteQueue,
  type DeliveryRouteView,
  type QueuedDeliveryView,
} from '../lib/api.ts'
import {
  clockAfter,
  durationText,
  kmText,
  moneyText,
  pathPoints,
  routeColor,
} from '../lib/route-format.ts'

import { LocateCustomerDialog } from './locate-customer-dialog.tsx'
import { MapView, type MapMarker, type MapPath } from './map-view.tsx'
import { RouteActions } from './route-actions.tsx'

/**
 * Rotas de entrega → Montar saída (F266).
 *
 * A fila do PDV ("Entregar na rota") à esquerda; quem vai sair em cima. O
 * Aeris separa o que vai a pé, divide o resto entre os motoboys e põe na
 * ordem. Cada rota tem a sua cor no mapa, e cada parada o horário previsto se
 * sair agora. O caixa pode passar uma parada de um para outro e mudar a
 * ordem; "Despachar" é o despacho de sempre, um pedido por vez, na ordem.
 */

const REASONS: Record<string, string> = {
  'no-point': 'sem ponto no mapa — localize o cliente',
  'too-far-on-foot': 'longe demais para ir a pé, e ninguém de moto nesta saída',
  'no-path': 'o mapa não achou caminho até lá',
}

const errorText = (cause: unknown): string =>
  cause instanceof ApiError ? cause.message : 'Não foi possível agora.'

type Arrangement = { id: string; version: number; documentIds: string[] }[]

export const RoutePlanner = ({
  origin,
  canRoute,
  canDispatch,
}: {
  origin: GeoPoint | null
  canRoute: boolean
  canDispatch: boolean
}): ReactNode => {
  const queryClient = useQueryClient()
  const queue = useQuery({
    queryKey: ['delivery-routes', 'queue'],
    queryFn: async ({ signal }) => await fetchRouteQueue(signal),
  })
  const couriers = useQuery({
    queryKey: ['delivery-routes', 'couriers'],
    queryFn: async ({ signal }) => await fetchCouriers(signal),
  })
  const planned = useQuery({
    queryKey: ['delivery-routes', 'routes', 'planned'],
    queryFn: async ({ signal }) => await fetchDeliveryRoutes('planned', signal),
  })

  /** O que ficou de fora na última montagem, com o motivo. */
  const [unassigned, setUnassigned] = useState<
    { salesDocumentId: string; reason: string }[]
  >([])
  const [skipped, setSkipped] = useState<ReadonlySet<string>>(new Set())
  const [going, setGoing] = useState<ReadonlySet<string>>(new Set())
  const [locating, setLocating] = useState<QueuedDeliveryView | null>(null)
  const [error, setError] = useState<string | null>(null)

  const items = queue.data ?? []
  const routes = useMemo(() => planned.data ?? [], [planned.data])
  const chosenItems = items.filter((item) => !skipped.has(item.salesDocumentId))

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['delivery-routes'] })
  }

  const plan = useMutation({
    mutationFn: async () =>
      await planDeliveryRoutes({
        documentIds: chosenItems.map((item) => item.salesDocumentId),
        couriers: [...going],
      }),
    onMutate: () => {
      setError(null)
    },
    onSuccess: async (result) => {
      setUnassigned(result.unassigned)
      await refresh()
    },
    onError: (cause: unknown) => {
      setError(errorText(cause))
    },
  })

  const rearrange = useMutation({
    mutationFn: async (arrangement: Arrangement) =>
      await rearrangeDeliveryRoutes({ routes: arrangement }),
    onMutate: () => {
      setError(null)
    },
    onSuccess: refresh,
    onError: async (cause: unknown) => {
      setError(errorText(cause))
      await refresh()
    },
  })

  const dispatch = useMutation({
    mutationFn: async (list: readonly DeliveryRouteView[]) => {
      for (const route of list) {
        await dispatchDeliveryRoute(route.id, route.version)
      }
      return list
    },
    onMutate: () => {
      setError(null)
    },
    onSuccess: async (list) => {
      notify.success(
        list.length === 1
          ? `A rota de ${list[0]?.courierName ?? ''} saiu.`
          : `${list.length} rotas saíram.`
      )
      setUnassigned([])
      await refresh()
    },
    onError: async (cause: unknown) => {
      setError(errorText(cause))
      await refresh()
    },
  })

  const undo = useMutation({
    mutationFn: async (list: readonly DeliveryRouteView[]) => {
      for (const route of list) await cancelDeliveryRoute(route.id)
    },
    onSuccess: async () => {
      setUnassigned([])
      await refresh()
    },
    onError: (cause: unknown) => {
      setError(errorText(cause))
    },
  })

  const unqueue = useMutation({
    mutationFn: async (documentId: string) => {
      await removeFromRouteQueue(documentId)
    },
    onSuccess: refresh,
    onError: (cause: unknown) => {
      setError(errorText(cause))
    },
  })

  const busy =
    plan.isPending ||
    rearrange.isPending ||
    dispatch.isPending ||
    undo.isPending

  /** O arranjo atual de todas as rotas planejadas, para o PUT. */
  const arrangement = (): Arrangement =>
    routes.map((route) => ({
      id: route.id,
      version: route.version,
      documentIds: route.stops.map((stop) => stop.salesDocumentId),
    }))

  const move = (routeId: string, index: number, delta: -1 | 1): void => {
    const next = arrangement()
    const target = next.find((route) => route.id === routeId)
    if (target === undefined) return
    const to = index + delta
    if (to < 0 || to >= target.documentIds.length) return
    const ids = [...target.documentIds]
    const [moved] = ids.splice(index, 1)
    if (moved === undefined) return
    ids.splice(to, 0, moved)
    target.documentIds = ids
    rearrange.mutate(next)
  }

  const handOver = (documentId: string, toRouteId: string): void => {
    const next = arrangement().map((route) => ({
      ...route,
      documentIds: route.documentIds.filter((id) => id !== documentId),
    }))
    const target = next.find((route) => route.id === toRouteId)
    if (target === undefined) return
    target.documentIds = [...target.documentIds, documentId]
    rearrange.mutate(next)
  }

  const colorOf = useMemo(() => {
    const colors = new Map<string, string>()
    routes.forEach((route, index) => colors.set(route.id, routeColor(index)))
    return colors
  }, [routes])

  const markers: MapMarker[] = useMemo(() => {
    const list: MapMarker[] =
      origin === null
        ? []
        : [
            {
              id: 'saida',
              point: origin,
              label: 'S',
              color: '#111827',
              title: 'Saída da loja',
            },
          ]
    for (const route of routes) {
      for (const stop of route.stops) {
        list.push({
          id: `${route.id}-${stop.salesDocumentId}`,
          point: stop,
          label: String(stop.position),
          color: colorOf.get(route.id) ?? '#2563eb',
          title: `${route.courierName}: ${stop.position}. ${stop.customerName}`,
        })
      }
    }
    return list
  }, [routes, origin, colorOf])

  const paths: MapPath[] = useMemo(
    () =>
      routes.flatMap((route) =>
        pathPoints(route.encodedPolyline).map((points, i) => ({
          id: `${route.id}-${i}`,
          points,
          color: colorOf.get(route.id) ?? '#2563eb',
        }))
      ),
    [routes, colorOf]
  )

  const now = new Date()
  const motoboys = (couriers.data ?? []).filter((c) => c.mode === 'motorcycle')
  const walkers = (couriers.data ?? []).filter((c) => c.mode === 'foot')

  if (origin === null) {
    return (
      <InlineAlert tone="warning">
        Diga de onde as entregas saem (aba Saída da loja) antes de montar a
        rota.
      </InlineAlert>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <Section
        title={`Fila da rota (${items.length})`}
        description='Os pedidos que o PDV mandou com "Entregar na rota". Desmarque o que não vai nesta saída.'
      >
        {queue.isPending ? (
          <Spinner label="Carregando a fila" />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Route aria-hidden="true" className="h-8 w-8" />}
            title="Nenhum pedido esperando rota"
            description='No PDV, ao fechar um pedido de entrega, escolha "Entregar na rota".'
          />
        ) : (
          <ul
            className="flex flex-col divide-y divide-(--border-subtle)"
            aria-label="Fila da rota"
          >
            {items.map((item) => (
              <li
                key={item.salesDocumentId}
                className="flex flex-wrap items-center gap-3 py-2"
              >
                <CheckboxField
                  id={`fila-${item.salesDocumentId}`}
                  checked={!skipped.has(item.salesDocumentId)}
                  disabled={!canRoute}
                  onCheckedChange={(checked) => {
                    setSkipped((current) => {
                      const next = new Set(current)
                      if (checked === true) next.delete(item.salesDocumentId)
                      else next.add(item.salesDocumentId)
                      return next
                    })
                  }}
                  label={
                    <span>
                      <span className="font-medium">
                        nº {item.number ?? '—'} ·{' '}
                        {item.customerTradeName ?? item.customerName}
                      </span>{' '}
                      <span className="tabular-nums text-(--text-secondary)">
                        {moneyText(item.totalAmount)}
                      </span>
                    </span>
                  }
                />
                <span className="min-w-0 flex-1 truncate text-(length:--text-xs) text-(--text-muted)">
                  {item.place === null ? (
                    <Badge tone="warning">sem ponto no mapa</Badge>
                  ) : (
                    item.place.label
                  )}
                </span>
                {item.plannedRouteId === null ? null : (
                  <Badge tone="info">na rota</Badge>
                )}
                {canRoute && item.place === null ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    leadingIcon={
                      <MapPin aria-hidden="true" className="h-4 w-4" />
                    }
                    onClick={() => {
                      setLocating(item)
                    }}
                  >
                    Localizar
                  </Button>
                ) : null}
                {canRoute ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={unqueue.isPending}
                    onClick={() => {
                      unqueue.mutate(item.salesDocumentId)
                    }}
                  >
                    Tirar da fila
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Section>

      {canRoute ? (
        <Section
          title="Quem sai agora"
          description="Marque os motoboys e quem entrega a pé nesta saída. Quem é o quê se muda na aba Entregadores."
        >
          {(couriers.data ?? []).length === 0 ? (
            <p className="text-(length:--text-sm) text-(--text-muted)">
              Ninguém cadastrado como entregador. Cadastre a pessoa em Clientes
              com o papel de entregador.
            </p>
          ) : (
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {[...motoboys, ...walkers].map((courier) => (
                <CheckboxField
                  key={courier.partyId}
                  id={`sai-${courier.partyId}`}
                  checked={going.has(courier.partyId)}
                  onCheckedChange={(checked) => {
                    setGoing((current) => {
                      const next = new Set(current)
                      if (checked === true) next.add(courier.partyId)
                      else next.delete(courier.partyId)
                      return next
                    })
                  }}
                  label={
                    <span className="flex items-center gap-1">
                      {courier.mode === 'foot' ? (
                        <Footprints aria-hidden="true" className="h-4 w-4" />
                      ) : (
                        <Bike aria-hidden="true" className="h-4 w-4" />
                      )}
                      {courier.name}
                      <span className="sr-only">
                        {courier.mode === 'foot' ? '(a pé)' : '(de moto)'}
                      </span>
                    </span>
                  }
                />
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={busy || going.size === 0 || chosenItems.length === 0}
              leadingIcon={<Route aria-hidden="true" className="h-4 w-4" />}
              onClick={() => {
                plan.mutate()
              }}
            >
              {plan.isPending
                ? 'Montando…'
                : routes.length > 0
                  ? 'Montar de novo'
                  : 'Montar rotas'}
            </Button>
          </div>
        </Section>
      ) : null}

      {error === null ? null : <InlineAlert tone="danger">{error}</InlineAlert>}

      {unassigned.length === 0 ? null : (
        <InlineAlert tone="warning">
          Ficaram de fora:{' '}
          {unassigned
            .map((entry) => {
              const item = items.find(
                (i) => i.salesDocumentId === entry.salesDocumentId
              )
              return `nº ${item?.number ?? '—'} (${REASONS[entry.reason] ?? entry.reason})`
            })
            .join('; ')}
        </InlineAlert>
      )}

      {routes.length === 0 ? null : (
        <Section
          title="As rotas desta saída"
          description="O horário é o previsto se sair agora, com 3 minutos parado em cada entrega."
        >
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
            <div className="flex flex-col gap-3">
              {routes.map((route) => (
                <article
                  key={route.id}
                  aria-label={`Rota de ${route.courierName}`}
                  className="flex flex-col gap-2 rounded-(--radius-md) border-l-4 bg-(--surface-sunken) py-2 pr-3 pl-3"
                  style={{ borderLeftColor: colorOf.get(route.id) }}
                >
                  <header className="flex flex-wrap items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="h-3 w-3 rounded-full"
                      style={{ background: colorOf.get(route.id) }}
                    />
                    <span className="font-medium">{route.courierName}</span>
                    <Badge tone="neutral">
                      {route.mode === 'foot' ? 'a pé' : 'moto'}
                    </Badge>
                    <span className="text-(length:--text-xs) text-(--text-secondary)">
                      {route.stops.length}{' '}
                      {route.stops.length === 1 ? 'entrega' : 'entregas'} ·{' '}
                      {durationText(route.durationSeconds)} ·{' '}
                      {kmText(route.distanceMeters)}
                      {route.returnSeconds === null
                        ? ''
                        : ` · volta ~${clockAfter(now, route.returnSeconds)}`}
                    </span>
                  </header>
                  {route.stops.length === 0 ? (
                    <p className="text-(length:--text-sm) text-(--text-muted)">
                      Nenhuma entrega para esta pessoa nesta saída.
                    </p>
                  ) : (
                    <ol className="flex flex-col gap-1">
                      {route.stops.map((stop, index) => (
                        <li
                          key={stop.salesDocumentId}
                          className="flex flex-wrap items-center gap-2 text-(length:--text-sm)"
                        >
                          <span className="w-5 text-right font-medium tabular-nums">
                            {stop.position}.
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="font-medium">
                              {stop.customerName}
                            </span>{' '}
                            <span className="text-(--text-muted)">
                              nº {stop.number ?? '—'} ·{' '}
                              {moneyText(stop.totalAmount)}
                            </span>
                            <span className="block truncate text-(length:--text-xs) text-(--text-muted)">
                              {stop.addressLabel}
                            </span>
                          </span>
                          <span className="tabular-nums text-(--text-secondary)">
                            ~{clockAfter(now, stop.arrivalSeconds)}
                          </span>
                          {canRoute ? (
                            <span className="flex items-center gap-1">
                              <Button
                                size="icon"
                                variant="ghost"
                                aria-label={`Subir ${stop.customerName}`}
                                disabled={busy || index === 0}
                                onClick={() => {
                                  move(route.id, index, -1)
                                }}
                              >
                                <ArrowUp
                                  aria-hidden="true"
                                  className="h-4 w-4"
                                />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                aria-label={`Descer ${stop.customerName}`}
                                disabled={
                                  busy || index === route.stops.length - 1
                                }
                                onClick={() => {
                                  move(route.id, index, 1)
                                }}
                              >
                                <ArrowDown
                                  aria-hidden="true"
                                  className="h-4 w-4"
                                />
                              </Button>
                              {routes.length > 1 ? (
                                <Select
                                  aria-label={`Passar ${stop.customerName} para`}
                                  value=""
                                  disabled={busy}
                                  onChange={(event) => {
                                    if (event.target.value !== '') {
                                      handOver(
                                        stop.salesDocumentId,
                                        event.target.value
                                      )
                                    }
                                  }}
                                  className="w-36"
                                >
                                  <option value="">Passar para…</option>
                                  {routes
                                    .filter((other) => other.id !== route.id)
                                    .map((other) => (
                                      <option key={other.id} value={other.id}>
                                        {other.courierName}
                                      </option>
                                    ))}
                                </Select>
                              ) : null}
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ol>
                  )}
                  {route.stops.length > 0 ? (
                    <RouteActions route={route} canSend={canRoute} />
                  ) : null}
                  {canDispatch && route.stops.length > 0 ? (
                    <div>
                      <Button
                        size="sm"
                        disabled={busy}
                        leadingIcon={
                          <Send aria-hidden="true" className="h-4 w-4" />
                        }
                        onClick={() => {
                          dispatch.mutate([route])
                        }}
                      >
                        Despachar a rota de {route.courierName}
                      </Button>
                    </div>
                  ) : null}
                </article>
              ))}
              <div className="flex flex-wrap gap-2">
                {canDispatch ? (
                  <Button
                    disabled={busy}
                    leadingIcon={
                      <Send aria-hidden="true" className="h-4 w-4" />
                    }
                    onClick={() => {
                      dispatch.mutate(
                        routes.filter((route) => route.stops.length > 0)
                      )
                    }}
                  >
                    {dispatch.isPending ? 'Despachando…' : 'Despachar todas'}
                  </Button>
                ) : null}
                {canRoute ? (
                  <Button
                    variant="secondary"
                    disabled={busy}
                    leadingIcon={
                      <Undo2 aria-hidden="true" className="h-4 w-4" />
                    }
                    onClick={() => {
                      undo.mutate(routes)
                    }}
                  >
                    Desfazer
                  </Button>
                ) : null}
              </div>
            </div>
            <MapView
              label="O mapa da saída"
              center={origin}
              zoom={13}
              markers={markers}
              paths={paths}
              className="h-96 lg:h-full lg:min-h-96"
            />
          </div>
        </Section>
      )}

      {locating === null ? null : (
        <LocateCustomerDialog
          key={locating.salesDocumentId}
          customer={{
            partyId: locating.customerPartyId,
            legalName: locating.customerName,
            tradeName: locating.customerTradeName,
            document: null,
            deliveries: 0,
            lastDeliveryAt: locating.queuedAt,
            addresses: [],
          }}
          open
          onOpenChange={(open) => {
            if (!open) setLocating(null)
          }}
          onLocated={() => {
            void refresh()
          }}
        />
      )}
    </div>
  )
}
